import {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  Autocomplete,
  type AutocompleteInputChangeReason,
  Checkbox,
  CircularProgress,
  ListItem,
  ListItemIcon,
  ListItemText,
  debounce,
} from '@mui/material';
import { Users } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import {
  type TeamFilterFragment,
  type Teams_Bool_Exp,
} from '@graphql/__generated__/graphql';
import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import type { QuickFilterProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import {
  AutocompletePaper,
  AutocompleteTextField,
  filterPopperProps,
  useFilterAutocompleteOpen,
} from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { useSchema } from '@modules/_shared/components/filters/useSchema';
import { useLocale } from '@shared/hooks/locale';

export const TEAM_FILTER_FRAGMENT = gql(/* GraphQL */ `
  fragment TeamFilter on teams {
    id
    name
  }
`);

const SEARCH_TEAMS = gql(/* GraphQL */ `
  query TeamFilterSearchTeams($where: teams_bool_exp!) {
    teams(where: $where, limit: 20, order_by: { name: asc }) {
      id
      ...TeamFilter
    }
  }
`);

const GET_DEFAULT_TEAMS = gql(/* GraphQL */ `
  query GetTeamsByIds($ids: [uuid!]!, $where: teams_bool_exp) {
    selected_teams: teams(where: { id: { _in: $ids } }) {
      id
      ...TeamFilter
    }

    initial_teams: teams(where: $where, limit: 20, order_by: { name: asc }) {
      id
      ...TeamFilter
    }
  }
`);

type TeamSelectBaseProps = {
  onClose?: () => void;
  filters?: Teams_Bool_Exp;
};

type TeamMultiSelectProps = TeamSelectBaseProps & {
  multiple?: true;
  onChange: (value: string[]) => void;
  defaultValue: string[];
};

type TeamSingleSelectProps = TeamSelectBaseProps & {
  multiple: false;
  onChange: (value: string | null) => void;
  defaultValue: string | null;
};

type TeamSelectProps = TeamMultiSelectProps | TeamSingleSelectProps;

export const TeamMultiSelect = (props: TeamSelectProps) => {
  const { onClose, filters } = props;
  const { t } = useLocale();
  const open = useFilterAutocompleteOpen();

  const [options, setOptions] = useState<TeamFilterFragment[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<TeamFilterFragment[]>(
    [],
  );

  const defaultIds = useMemo((): string[] => {
    if (props.multiple === false) {
      return props.defaultValue != null ? [props.defaultValue] : [];
    }
    return props.defaultValue;
  }, [props.multiple, props.defaultValue]);

  const [searchTeams, { loading: searchLoading }] = useLazyQuery(SEARCH_TEAMS, {
    notifyOnNetworkStatusChange: true,
  });

  const { data, loading: defaultLoading } = useQuery(GET_DEFAULT_TEAMS, {
    variables: {
      ids: defaultIds,
      where: { id: { _nin: defaultIds }, ...(filters ?? {}) },
    },
  });

  const updateOptions = useMemo(
    () =>
      (newValue: TeamFilterFragment[], initialTeams?: TeamFilterFragment[]) => {
        const combined = [...newValue, ...(initialTeams ?? [])];
        const uniqueCombined = combined.filter(
          (team, idx, arr) => arr.findIndex(t => t.id === team.id) === idx,
        );
        setOptions(uniqueCombined);
      },
    [],
  );

  useEffect(() => {
    if (data && options.length === 0 && !inputValue) {
      setSelectedOptions(data.selected_teams);
      updateOptions(data.selected_teams, data.initial_teams);
    }
  }, [data, inputValue, options.length, updateOptions]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        const { data: searchData } = await searchTeams({
          variables: {
            where: {
              name: { _ilike: `%${value}%` },
              id: { _nin: defaultIds },
              ...(filters ?? {}),
            },
          },
        });
        if (!searchData?.teams?.length) {
          setOptions([]);
        } else {
          setOptions(searchData?.teams ?? []);
        }
      }, 275),
    [defaultIds, searchTeams, filters],
  );

  const handleSearch = useCallback(
    (searchValue: string) => {
      if (searchValue) {
        debouncedSearch(searchValue);
      } else {
        updateOptions(data?.selected_teams ?? [], data?.initial_teams);
      }
    },
    [debouncedSearch, data, updateOptions],
  );

  const handleToggleOptions = useCallback(
    (_: any, newValue: TeamFilterFragment[] | TeamFilterFragment | null) => {
      const newSelectedOptions =
        newValue != null
          ? Array.isArray(newValue)
            ? newValue
            : [newValue]
          : [];

      setSelectedOptions(newSelectedOptions);

      if (props.multiple === false) {
        props.onChange(
          newSelectedOptions.length > 0 ? newSelectedOptions[0].id : null,
        );
      } else {
        props.onChange(newSelectedOptions.map(v => v.id));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.multiple, props.onChange],
  );

  const handleInputChange = useCallback(
    (
      _: SyntheticEvent<Element, Event>,
      newValue: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason !== 'input') {
        return;
      }

      setInputValue(newValue);
      handleSearch(newValue);
    },
    [handleSearch],
  );

  const loading = searchLoading || defaultLoading;

  return (
    <Autocomplete
      multiple={props.multiple !== false}
      disableCloseOnSelect
      openOnFocus
      open={open}
      disablePortal
      disableClearable={props.multiple !== false}
      size="small"
      clearOnBlur={false}
      {...filterPopperProps}
      value={
        props.multiple !== false ? selectedOptions : selectedOptions[0] ?? null
      }
      loading={loading}
      loadingText={t('Loading...')}
      onClose={onClose}
      sx={{ width: 350 }}
      options={options}
      getOptionLabel={option => option.name}
      filterOptions={x => x}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={handleToggleOptions}
      renderInput={params => (
        <AutocompleteTextField
          {...params}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: loading ? (
                <CircularProgress color="inherit" size={16} />
              ) : (
                params.slotProps.input.endAdornment
              ),
            },
          }}
        />
      )}
      renderOption={(optionProps, option, { selected }) => (
        <ListItem {...optionProps} key={option.id} dense sx={{ py: 0.5 }}>
          {props.multiple !== false && (
            <ListItemIcon sx={{ minWidth: 0, mr: 0.5 }}>
              <Checkbox
                size="small"
                edge="start"
                checked={selected}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
          )}
          <ListItemText primary={option.name} />
        </ListItem>
      )}
      noOptionsText={t('No options')}
      slots={{
        paper: AutocompletePaper,
      }}
    />
  );
};

export const TeamFilter = ({
  label,
  path,
  where,
  addWhereClause,
  deleteWhereClause,
  queryParamsScope,
  disabled,
}: QuickFilterProps) => {
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);
  const { getValueFromPath } = useSchema();
  const defaultValue = getValueFromPath(path, where)?.id?._in ?? [];

  const handleChange = (value: string[]) => {
    let newWhere = deleteWhereClause(where, path, true);
    if (value.length > 0) {
      newWhere = addWhereClause(newWhere, [...path, 'id', '_in'], value);
    }
    setFiltersParams(newWhere);
  };
  const handleDelete =
    defaultValue?.length > 0 ? () => handleChange([]) : undefined;

  return (
    <FilterChip
      label={label}
      icon={<Users size={16} />}
      onDelete={handleDelete}
      disabled={disabled?.(where) ?? false}
      renderFilter={({ handleClose }) => (
        <TeamMultiSelect
          onClose={handleClose}
          onChange={handleChange}
          defaultValue={defaultValue}
        />
      )}
    />
  );
};
